import { useCallback, useRef, useState } from 'react';
import useCards from '@/hooks/cards/useCards';
import useStorage from '@/hooks/storage/useStorage';
import useIsNative from '@/hooks/native/useIsNative';
import useNativeMessages from '@/hooks/native/useNativeMessages';
import { Zip, AsyncZipDeflate } from 'fflate';

const backupDir = 'backups';
const backupBase = 'backup';
const MAX_ZIP_RESULT_MB = 12; // Target per ZIP result for web/native share
const NATIVE_STORE_SAFE_MB = 16; // Conservative native postMessage size limit

export const useBackup = () => {
    // Native connection
    const isNative = useIsNative();
    const { sendNativeMessage } = useNativeMessages();
    const { saveFileBase64, readFileBase64, deleteFile } = useStorage();

    // Cards
    const { cards, getMediaItemPath } = useCards();

    const isBackingUp = useRef(false);
    // A number between 0 and 1
    const [backupProgress, setBackupProgress] = useState(0);
    // Step indicator for UI
    type BackupStep = 'none' | 'preparing' | 'collectingMedia' | 'readingFiles' | 'creatingZip' | 'finalizingZip' | 'convertingToDataURL' | 'storingZip' | 'done';
    const [backupStep, setBackupStep] = useState<BackupStep>('none');
    // Results: base64 URIs for web (one per ZIP)
    const [backupResults, setBackupResults] = useState<string[]>([]);
    // Final ZIP sizes in MB (one per ZIP)
    const [backupSizesInMb, setBackupSizesInMb] = useState<number[]>([]);
    // Whether each ZIP was stored on native filesystem
    const [backupStoredOnNative, setBackupStoredOnNative] = useState<boolean[]>([]);
    // Native paths per ZIP
    const [backupPaths, setBackupPaths] = useState<string[]>([]);

    const startBackup = useCallback(async () => {
        if (isBackingUp.current) return;

        try {
            setBackupStep('preparing');
            setBackupProgress(0.01);
            setBackupResults([]);
            setBackupSizesInMb([]);
            setBackupStoredOnNative([]);
            setBackupPaths([]);
            isBackingUp.current = true;

            // Delete an existing backup file
            if (isNative) {
                // Best-effort cleanup of last run's known file names
                for (let i = 1; i <= 20; i++) {
                    deleteFile(`${backupDir}/${backupBase}-${i}.zip`);
                }
            }

            // Collect all media file paths referenced by cards
            setBackupStep('collectingMedia');
            const cardsMediaPaths: string[][] = [];
            for (const card of cards) {
                const paths: string[] = [];
                for (const photo of card.photos) paths.push(getMediaItemPath(photo.id, 'photo'));
                for (const audio of card.audio ?? []) paths.push(getMediaItemPath(audio.id, 'audio'));
                cardsMediaPaths.push(paths);
            }

            // Phase 1: Reading files (0% - 50%)
            setBackupStep('readingFiles');
            const totalReadSteps = Math.max(1, cardsMediaPaths.reduce((sum, ar) => sum + ar.length, 0) + 1); // +1 for data.json later
            let readStep = 0;
            const files: Record<string, Uint8Array> = {};

            // Helper: convert base64 (optionally data URL) -> Uint8Array
            const base64ToU8 = (input: string): Uint8Array => {
                const b64 = input.includes(';base64,')
                    ? input.split(';base64,')[1]
                    : input;
                // atob returns a binary string
                const bin = atob(b64);
                const len = bin.length;
                const out = new Uint8Array(len);
                for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
                return out;
            };

            // Read all media as bytes
            for (const paths of cardsMediaPaths) {
                for (const path of paths) {
                    const dataUrl = await readFileBase64(path);
                    if (dataUrl) files[path] = base64ToU8(dataUrl);
                    readStep += 1;
                    setBackupProgress(0.5 * (readStep / totalReadSteps));
                }
            }

            // We'll add data.json per ZIP group below; still advance progress a step
            readStep += 1;
            setBackupProgress(0.5 * (readStep / totalReadSteps));

            // Phase 2: Partition cards into groups (~<= 12 MB per ZIP, full cards only)
            // Use uncompressed byte sums to conservatively cap group size at ~11 MB
            const UNCOMPRESSED_LIMIT = Math.floor((MAX_ZIP_RESULT_MB - 1) * 1024 * 1024); // ~11 MB
            type Group = { cardIndexes: number[]; paths: string[]; totalBytes: number };
            const groups: Group[] = [];
            let current: Group = { cardIndexes: [], paths: [], totalBytes: 0 };
            for (let ci = 0; ci < cards.length; ci++) {
                const paths = cardsMediaPaths[ci];
                const cardBytes = paths.reduce((s, p) => s + (files[p]?.length ?? 0), 0);
                // If adding this card would exceed the limit and current has content, finalize current
                if (current.totalBytes > 0 && current.totalBytes + cardBytes > UNCOMPRESSED_LIMIT) {
                    groups.push(current);
                    current = { cardIndexes: [], paths: [], totalBytes: 0 };
                }
                current.cardIndexes.push(ci);
                current.paths.push(...paths);
                current.totalBytes += cardBytes;
            }
            if (current.cardIndexes.length > 0) groups.push(current);

            // Phase 3: Zipping groups (50% - 100%) using async streaming for progress
            setBackupStep('creatingZip');
            const totalBytesToZip = Object.values(files).reduce((sum, data) => sum + data.length, 0);
            let processedBytes = 0;
            const results: string[] = [];
            const sizesMb: number[] = [];
            const storedFlags: boolean[] = [];
            const pathsOut: string[] = [];

            // For each group, create a ZIP with that group's files + data.json for those cards
            for (let gi = 0; gi < groups.length; gi++) {
                const group = groups[gi];
                // Build the files map for this group
                const groupEntries: [string, Uint8Array][] = [];
                for (const p of group.paths) {
                    const data = files[p];
                    if (data) groupEntries.push([p, data]);
                }
                // Add data.json with only the group's cards
                const groupCards = group.cardIndexes.map((ci) => cards[ci]);
                const json = JSON.stringify(groupCards, null, 4);
                groupEntries.push(['data.json', new TextEncoder().encode(json)]);

                // Stream ZIP
                const outChunks: Uint8Array[] = [];
                let zipResolve!: () => void;
                let zipReject!: (e?: unknown) => void;
                const zipDone = new Promise<void>((resolve, reject) => {
                    zipResolve = resolve; zipReject = reject;
                });
                const zipper = new Zip((err, chunk, final) => {
                    if (err) { zipReject(err); return; }
                    if (chunk) outChunks.push(chunk);
                    if (final) zipResolve();
                });
                for (const [name, data] of groupEntries) {
                    const entry = new AsyncZipDeflate(name, { level: 9 });
                    // Track progress by drained bytes (approximate)
                    (entry as unknown as { d: { ondrain?: (size: number) => void } }).d.ondrain = (size: number) => {
                        processedBytes += size;
                        setBackupProgress(0.5 + 0.5 * (processedBytes / totalBytesToZip));
                    };
                    zipper.add(entry);
                    entry.push(data, true);
                }
                setBackupStep('finalizingZip');
                zipper.end();
                await zipDone;

                // Compute size and convert to data URL
                const zipBytes = outChunks.reduce((sum, c) => sum + c.byteLength, 0);
                sizesMb.push(zipBytes / (1024 * 1024));
                setBackupStep('convertingToDataURL');
                const blobParts = outChunks.map((c) =>
                    c.byteOffset === 0 && c.byteLength === c.buffer.byteLength
                        ? (c.buffer as ArrayBuffer)
                        : (c.buffer as ArrayBuffer).slice(c.byteOffset, c.byteOffset + c.byteLength)
                );
                const zipBlob = new Blob(blobParts, { type: 'application/zip' });
                const dataUrlZip: string = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onerror = () => reject(reader.error ?? new Error('Failed to read ZIP blob'));
                    reader.onload = () => resolve(String(reader.result));
                    reader.readAsDataURL(zipBlob);
                });
                results.push(dataUrlZip);

                // Store on native (per group) if size is safe
                let stored = false;
                if (isNative && sizesMb[gi] <= NATIVE_STORE_SAFE_MB) {
                    setBackupStep('storingZip');
                    const path = `${backupDir}/${backupBase}-${gi + 1}.zip`;
                    try {
                        await saveFileBase64(path, dataUrlZip);
                        stored = true;
                        pathsOut.push(path);
                    } catch (err) {
                        console.warn('Native save failed for group', gi + 1, err);
                        stored = false;
                        pathsOut.push(path);
                    }
                } else if (isNative) {
                    // Still record a path for sharing intent if needed
                    pathsOut.push(`${backupDir}/${backupBase}-${gi + 1}.zip`);
                } else {
                    pathsOut.push(`${backupDir}/${backupBase}-${gi + 1}.zip`);
                }
                storedFlags.push(stored);
            }

            setBackupResults(results);
            setBackupSizesInMb(sizesMb);
            setBackupStoredOnNative(storedFlags);
            setBackupPaths(pathsOut);
            setBackupProgress(1);
            setBackupStep('done');
        } catch (e) {
            console.error('Backup failed', e);
            alert(JSON.stringify(e, null, 2));
            setBackupProgress(0);
            setBackupResults([]);
            setBackupStep('none');
        }

        isBackingUp.current = false;
    }, [cards, deleteFile, getMediaItemPath, isNative, readFileBase64, saveFileBase64]);

    const shareBackup = useCallback((index: number) => {
        if (isBackingUp.current) return;
        const dataUrl = backupResults[index];
        if (!dataUrl) return;

        if (isNative && backupStoredOnNative[index]) {
            sendNativeMessage('share.file', backupPaths[index]);
            return;
        }

        if (isNative && !backupStoredOnNative[index]) {
            const sizeMb = backupSizesInMb[index];
            const sizeInfo = isFinite(sizeMb) ? ` (${sizeMb.toFixed(1)} MB)` : '';
            sendNativeMessage(
                'alert.show',
                `Deze backup${sizeInfo} is niet lokaal opgeslagen en kan niet direct gedownload worden in de app. Open de website in je browser om te downloaden.`,
            );
            return;
        }

        // Web: trigger a download of this ZIP
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `${backupBase}-${index + 1}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [backupPaths, backupResults, backupSizesInMb, backupStoredOnNative, isNative, sendNativeMessage]);

    return {
        backupProgress,
        backupStep,
        backupResults,
        backupSizesInMb,
        backupStoredOnNative,
        backupPaths,
        startBackup,
        shareBackup,
    }
};
